// © Microsoft Corporation. All rights reserved.
import { ImageFetchFailed } from "../AppErrors";
import { logger } from "../wwCommon";
/**
 * Bing image resizer for content images.
 */
const BingThumbnailBaseUrl = "//www.bing.com/th";
/**
 * Image size mapping for various content types.
 * Please Note: tall and double wide cards image sizes are not supported yet
 */
export const imageSizeMap = {
    ContentPreview: { width: 300, height: 174 },
    HalfPane: { width: 624, height: 350 },
    HeadToHead: [{ width: 312, height: 350 }, { width: 312, height: 350 }],
    Mosaic: [{ width: 312, height: 350 }, { width: 312, height: 175 }, { width: 312, height: 175 }],
    Triptych: [{ width: 208, height: 350 }, { width: 208, height: 350 }, { width: 208, height: 350 }],
    Image_1x1y: { width: 90, height: 90 },
    _2x_3y: { width: 612, height: 462 } // Windows feed Infopane image size
};
/**
 * Fetches the image for given image metadata and image size.
 * @param imageMetadata - The image metadata
 * @param imageSize - The image size
 * @param options - Options for the image fetch
  */
export function fetchImage(imageMetadata, imageSize, options) {
    options = options || {};
    const url = getImageUrl(imageMetadata, imageSize, options.overrideImageQuality);
    if (url) {
        fetch(url).then(response => {
            if (!response.ok) {
                throw (`Image call failed with status [${response.status}].`);
            }
        }).catch(reason => {
            const appError = options.appError || ImageFetchFailed;
            logger.error(Object.assign(Object.assign({}, appError), { message: "Image fetch failed", pb: Object.assign(Object.assign(Object.assign({}, appError.pb), options.appErrorPB), { customMessage: `Error: ${reason}. Url: ${url}` }) }));
        });
    }
}
/**
 * Returns the image url.
 * NOTE: Make sure the Url generated matches what will be requested in the main thread.
 * @param imageMetadata - The image metadata
 * @param imageSize - The image size
 * @param overrideImageQuality - Should we override the default Peregrine quality with a higher one?
 * @returns The image url.
 */
function getImageUrl(imageMetadata, imageSize, overrideImageQuality) {
    var _a;
    if (!imageMetadata || (!imageMetadata.id && !imageMetadata.url) || !imageSize || !imageSize.height || !imageSize.width) {
        return null;
    }
    if ((_a = imageMetadata.url) === null || _a === void 0 ? void 0 : _a.includes(BingThumbnailBaseUrl)) {
        return getBingImageUrl(imageMetadata, imageSize, overrideImageQuality);
    }
    if (!imageMetadata.id) {
        imageMetadata.id = getImageIdFromUrl(imageMetadata.url);
    }
    let auxQsp = "";
    const { id, focalRegion } = imageMetadata;
    if (focalRegion) {
        const focusX = midPoint(focalRegion.x1, focalRegion.x2);
        const focusY = midPoint(focalRegion.y1, focalRegion.y2);
        if (focusX != null && focusY != null) {
            auxQsp += `&x=${focusX}&y=${focusY}`;
        }
    }
    const quality = overrideImageQuality ? 90 : 60;
    return `//img-s-msn-com.akamaized.net/tenant/amp/entityid/${id}?w=${imageSize.width}&h=${imageSize.height}&q=${quality}&m=6&f=jpg${auxQsp}&u=t`;
}
/**
 * Returns the Bing image url.
 * NOTE: Make sure the Url generated matches what will be requested in the main thread.
 * @param imageMetadata - The image metadata
 * @param imageSize - The image size
 * @returns The image url.
 */
function getBingImageUrl(imageMetadata, imageSize, overrideImageQuality) {
    if (!imageMetadata || !imageSize || !imageSize.height || !imageSize.width) {
        return null;
    }
    const auxQsp = "&rs=1";
    const { url } = imageMetadata;
    const { height, width } = imageSize;
    // NOTE: I know these could be written directly in the return string, but defining them to call them out and easier updating.
    const quality = overrideImageQuality ? 90 : 60;
    const mode = 1;
    return `${url}&w=${width}&h=${height}&qlt=${quality}&c=${mode}${auxQsp}`;
}
/**
 * Returns the (integer) midpoint between two integers.
 * @param min - The min number
 * @param max - The max number
 * @returns The (integer) midpoint between two integers
 */
function midPoint(min, max) {
    if (min != null && max != null) {
        return Math.floor((min + max) / 2);
    }
}
/**
 * Returns ImageId from URL
 * @param url - the url string
 */
function getImageIdFromUrl(url) {
    if (!url) {
        return;
    }
    const imageData = url.split("/");
    const imageId = imageData[imageData.length - 1];
    return imageId;
}
//# sourceMappingURL=Image.js.map